import { animate, motion, useInView, useReducedMotion } from '@/lib/motion';
import { useEffect, useRef, useState } from 'react';

/**
 * Product-truth stats band. Replaces the fabricated customer-logo bar —
 * every number here is a property of the product, not an invented customer
 * metric. Values count up when the band scrolls into view.
 */
const STATS: Array<{
  value: number;
  format: (n: number) => string;
  label: string;
}> = [
  {
    value: 380,
    format: (n) => `${Math.round(n)}ms`,
    label: 'Typical first response',
  },
  {
    value: 24,
    format: (n) => `${Math.round(n)}/7`,
    label: 'Always answering',
  },
  {
    value: 2,
    format: (n) => `${Math.round(n)}`,
    label: 'Channels, one knowledge base',
  },
  {
    value: 10,
    format: (n) => `< ${Math.round(n)} min`,
    label: 'From signup to live',
  },
];

export function StatsBand(): JSX.Element {
  return (
    <section className="border-y border-border bg-muted/20 py-12">
      <div className="container">
        <div className="grid grid-cols-2 gap-8 text-center md:grid-cols-4">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ delay: i * 0.08 }}
            >
              <CountUp
                target={s.value}
                format={s.format}
                className="text-3xl font-bold tracking-tight sm:text-4xl"
              />
              <p className="mt-1 text-sm text-muted-foreground">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CountUp({
  target,
  format,
  className,
}: {
  target: number;
  format: (n: number) => string;
  className?: string;
}): JSX.Element {
  const ref = useRef<HTMLParagraphElement | null>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const reduced = useReducedMotion();
  const [display, setDisplay] = useState(() => format(reduced ? target : 0));

  useEffect(() => {
    if (!inView) return;
    if (reduced) {
      setDisplay(format(target));
      return;
    }
    const controls = animate(0, target, {
      duration: 1.4,
      ease: 'easeOut',
      onUpdate: (v) => setDisplay(format(v)),
    });
    return () => controls.stop();
  }, [inView, reduced, target, format]);

  return (
    <p ref={ref} className={className}>
      {display}
    </p>
  );
}
